import { alpha } from '@mui/material/styles';
import { svgIconClasses } from '@mui/material/SvgIcon';
import { iconButtonClasses } from '@mui/material/IconButton';
import colors from '../theme/colors';
import palette from '../theme/palette';

import type { IconButtonProps } from './IconButton';
import defaultProps from './defaultProps';

const borderWidthValue = '1px';
const borderStyleValue = 'solid';

const borderStyles = {
  borderRadius: '6px',
  borderWidth: borderWidthValue,
  borderStyle: borderStyleValue,
};

const paddings = {
  small: '6px',
  medium: '7px',
  large: '9px',
};

const fontSizes = {
  small: '18px',
  medium: 'inherit',
  large: '28px',
};

const getSize = (variant: IconButtonProps['variant'], size: IconButtonProps['size'] = defaultProps.size) => ({
  props: { variant, size },
  style: {
    [`& .${svgIconClasses.root}`]: {
      fontSize: fontSizes[size],
    },
    padding: paddings[size],
  },
});

const getSizesForVariant = (variant: IconButtonProps['variant']) => [
  getSize(variant, 'small'),
  getSize(variant, 'medium'),
  getSize(variant, 'large'),
];

const variants = [
  ...getSizesForVariant('circle'),
  ...getSizesForVariant('squared'),
  ...getSizesForVariant('squared-contained'),
  ...getSizesForVariant('squared-text'),
  {
    props: { variant: 'circle' },
    style: {
      borderColor: 'transparent',
      borderWidth: borderWidthValue,
      borderStyle: borderStyleValue,
    },
  },
  {
    props: { variant: 'squared' },
    style: borderStyles,
  },
  {
    props: { variant: 'squared', color: 'internal' },
    style: {
      backgroundColor: 'transparent',
      color: palette.internal.main,
      '&:hover': {
        backgroundColor: 'transparent',
        color: colors.internal[700],
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: 'transparent',
        },
      },
    },
  },
  {
    props: { variant: 'squared', color: 'primary' },
    style: {
      backgroundColor: 'transparent',
      color: palette.primary.main,
      '&:hover': {
        backgroundColor: 'transparent',
        color: colors.brandPrimary[700],
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: 'transparent',
        },
      },
    },
  },
  {
    props: { variant: 'squared', color: 'error' },
    style: {
      backgroundColor: 'transparent',
      color: palette.error.main,
      '&:hover': {
        backgroundColor: 'transparent',
        color: palette.error.dark,
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: 'transparent',
        },
      },
    },
  },
  {
    props: { variant: 'squared-contained' },
    style: borderStyles,
  },
  {
    props: { variant: 'squared-contained', color: 'internal' },
    style: {
      borderColor: palette.internal.main,
      backgroundColor: palette.internal.main,
      color: palette.common.white,
      '&:hover': {
        backgroundColor: colors.internal[700],
        borderColor: colors.internal[700],
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: palette.internal.main,
        },
      },
    },
  },
  {
    props: { variant: 'squared-contained', color: 'primary' },
    style: {
      borderColor: palette.primary.main,
      backgroundColor: palette.primary.main,
      color: palette.common.white,
      '&:hover': {
        backgroundColor: palette.primary.dark,
        borderColor: palette.primary.dark,
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: palette.primary.main,
        },
      },
    },
  },
  {
    props: { variant: 'squared-contained', color: 'success' },
    style: {
      borderColor: palette.success.main,
      backgroundColor: palette.success.main,
      color: palette.common.white,
      '&:hover': {
        backgroundColor: colors.success[700],
        borderColor: colors.success[700],
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: palette.success.main,
        },
      },
    },
  },
  {
    props: { variant: 'squared-contained', color: 'secondary' },
    style: {
      backgroundColor: palette.common.white,
      borderColor: palette.common.white,
      color: palette.primary.main,
      '&:hover': {
        backgroundColor: '#F3F3F3',
        borderColor: '#F3F3F3',
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: palette.common.white,
        },
      },
    },
  },
  {
    props: { variant: 'squared-contained', color: 'error' },
    style: {
      borderColor: palette.error.main,
      backgroundColor: palette.error.main,
      color: palette.common.white,
      '&:hover': {
        backgroundColor: palette.error.dark,
        borderColor: palette.error.dark,
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: palette.error.main,
        },
      },
    },
  },
  {
    props: { variant: 'squared-text' },
    style: {
      ...borderStyles,
      borderColor: 'transparent',
    },
  },
  {
    props: { variant: 'squared-text', color: 'internal' },
    style: {
      backgroundColor: alpha(palette.internal.main, 0.06),
      color: palette.internal.main,
      '&:hover': {
        backgroundColor: alpha(palette.internal.main, 0.06 + palette.action.hoverOpacity),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: alpha(palette.internal.main, 0.06),
        },
      },
    },
  },
  {
    props: { variant: 'squared-text', color: 'primary' },
    style: {
      backgroundColor: alpha(palette.primary.main, 0.06),
      color: palette.primary.main,
      '&:hover': {
        backgroundColor: alpha(palette.primary.main, 0.06 + palette.action.hoverOpacity),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: alpha(palette.primary.main, 0.06),
        },
      },
    },
  },
  {
    props: { variant: 'squared-text', color: 'secondary' },
    style: {
      backgroundColor: colors.basic.white,
      color: palette.primary.main,
      '&:hover': {
        backgroundColor: '#F3F3F3',
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: colors.basic.white,
        },
      },
    },
  },
  {
    props: { variant: 'squared-text', color: 'success' },
    style: {
      backgroundColor: alpha(palette.success.main, 0.06),
      color: palette.success.main,
      '&:hover': {
        backgroundColor: alpha(palette.success.main, 0.06 + palette.action.hoverOpacity),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: alpha(palette.success.main, 0.06),
        },
      },
    },
  },
  {
    props: { variant: 'squared-text', color: 'error' },
    style: {
      backgroundColor: alpha(palette.error.main, 0.06),
      color: palette.error.main,
      '&:hover': {
        backgroundColor: alpha(palette.error.main, 0.06 + palette.action.hoverOpacity),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: alpha(palette.error.main, 0.06),
        },
      },
    },
  },
  {
    props: { variant: 'squared-contained', disabled: true },
    style: {
      [`&.${iconButtonClasses.disabled}`]: {
        borderColor: colors.solidGray[400],
        backgroundColor: palette.action.disabledBackground,
        color: palette.common.white,
        '&:hover': {
          borderColor: colors.solidGray[400],
          backgroundColor: palette.action.disabledBackground,
          color: palette.common.white,
          // Reset on touch devices, it doesn't add specificity
          '@media (hover: none)': {
            backgroundColor: palette.action.disabledBackground,
          },
        },
      },
    },
  },
  {
    props: { variant: 'squared-text', disabled: true },
    style: {
      [`&.${iconButtonClasses.disabled}`]: {
        borderColor: colors.solidGray[100],
        backgroundColor: colors.transparentGray[100],
        color: palette.action.disabled,
        '&:hover': {
          borderColor: colors.solidGray[100],
          backgroundColor: colors.transparentGray[100],
          color: palette.action.disabled,
          // Reset on touch devices, it doesn't add specificity
          '@media (hover: none)': {
            backgroundColor: colors.transparentGray[100],
          },
        },
      },
    },
  },
];

export default variants;
